import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { getResumeBlob } from './docxPreview.util';
import styles from './ResumeDialog.module.scss';

interface IDocxPreviewProps {
  docLink: string;
  rawResume?: string;
}

const DocxPreview = ({ docLink, rawResume }: IDocxPreviewProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const styleRef = useRef<HTMLDivElement>(null);
  const renderTokenRef = useRef(0);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isRendering, setIsRendering] = useState(true);

  const renderPreview = useCallback(async () => {
    const bodyContainer = bodyRef.current;
    if (!bodyContainer || !docLink) return;

    const renderToken = ++renderTokenRef.current;
    setIsRendering(true);
    setPreviewError(null);

    try {
      const { renderAsync } = await import('docx-preview');
      const blob = await getResumeBlob(docLink, rawResume);

      if (renderToken !== renderTokenRef.current) return;

      bodyContainer.innerHTML = '';
      if (styleRef.current) {
        styleRef.current.innerHTML = '';
      }

      await renderAsync(
        blob,
        bodyContainer,
        styleRef.current ?? undefined,
        {
          className: 'docx-resume-preview',
          inWrapper: true,
          ignoreWidth: true,
          ignoreHeight: false,
        },
      );
    } catch (error) {
      if (renderToken !== renderTokenRef.current) return;
      const message =
        error instanceof Error ? error.message : 'Failed to preview resume';
      setPreviewError(message);
      console.error('docx preview error', error);
    } finally {
      if (renderToken === renderTokenRef.current) {
        setIsRendering(false);
      }
    }
  }, [docLink, rawResume]);

  useEffect(() => {
    renderPreview();
    return () => {
      renderTokenRef.current += 1;
      if (bodyRef.current) {
        bodyRef.current.innerHTML = '';
      }
      if (styleRef.current) {
        styleRef.current.innerHTML = '';
      }
    };
  }, [renderPreview]);

  return (
    <div className={styles.docxPreviewWrapper}>
      <div ref={styleRef} className={styles.docxStyleHost} />
      {isRendering && (
        <Typography className='kfic2-bodyText' data-testid='docx-preview-loading'>
          Loading preview...
        </Typography>
      )}
      {previewError && (
        <Typography
          className='kfic2-bodyText'
          color='error'
          data-testid='docx-preview-error'
        >
          {previewError}
        </Typography>
      )}
      <div
        ref={bodyRef}
        className={styles.docxPreviewContainer}
        data-testid='docx-preview-container'
      />
    </div>
  );
};

export default DocxPreview;
